/**
 * PDP Engagement Tracker
 *
 * Captures intent signals between view_item and add_to_cart that GA4 doesn't
 * track by default. Helps distinguish "user evaluated and bounced" from "user
 * never engaged."
 *
 * Events emitted (to both gtag and dataLayer):
 *   - pdp_loaded             fires once on init; baseline event carrying the
 *                            availability snapshot for the page (landing
 *                            variant in stock?, % of sizes available, etc.)
 *   - select_variant         user picked a size/color
 *   - view_size_guide        opened the Tabela de medidas accordion
 *   - image_zoom             opened the zoom dialog on a product image
 *   - carousel_scroll        navigated the product image carousel
 *   - scroll_depth_pdp       reached 25/50/75/100% of the PDP
 *   - atc_click_failed       clicked ATC while disabled (variant not selected / sold out)
 *                            or got a server-side cart error after click
 *
 * Every event carries the product + availability context so any downstream
 * funnel metric can be segmented by availability state.
 *
 * Note: view_shipping_calculator is intentionally NOT implemented — this
 * theme uses Yampi for checkout and has no PDP shipping cost widget. Add a
 * hook here if a shipping calculator is later added to the PDP.
 */

import { ThemeEvents } from '@theme/events';

const SCRIPT_TAG_ID = 'pdp-engagement-product-context';

/**
 * @typedef {Object} ProductContext
 * @property {string|number} product_id
 * @property {string} product_handle
 * @property {string} product_title
 * @property {string} [product_type]
 * @property {string} [product_vendor]
 * @property {string} [variant_id]
 * @property {number} [price]
 * @property {string} [currency]
 */

/** Read product context injected by product-information.liquid. */
function readProductContext() {
  const tag = document.getElementById(SCRIPT_TAG_ID);
  if (!tag?.textContent) return null;
  try {
    return JSON.parse(tag.textContent);
  } catch {
    return null;
  }
}

/**
 * Defensive analytics push. Sends to gtag (GA4) and dataLayer (GTM).
 * Both may be absent in design mode or before consent.
 *
 * @param {string} eventName
 * @param {Object} params
 */
function track(eventName, params) {
  if (typeof window.gtag === 'function') {
    try { window.gtag('event', eventName, params); } catch {}
  }
  if (Array.isArray(window.dataLayer)) {
    try { window.dataLayer.push({ event: eventName, ...params }); } catch {}
  }
}

/** Build an event payload combining product context with event-specific params. */
function payload(context, extra = {}) {
  return { ...(context || {}), ...extra };
}

/** ---------- 1. select_variant ---------- */
function bindSelectVariant(context) {
  document.addEventListener(ThemeEvents.variantSelected, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    track('select_variant', payload(context, {
      option_value_id: detail.id || null,
    }));
  });
}

/** ---------- 2. view_size_guide ---------- */
// Tabela de medidas is rendered as an accordion-row block. Heading text is
// merchant-controlled, so match heuristically (case-insensitive contains).
const SIZE_GUIDE_KEYWORDS = ['tabela de medidas', 'tabela', 'medidas', 'size guide', 'size chart'];

function isSizeGuideAccordion(detailsEl) {
  const summary = detailsEl.querySelector('summary');
  if (!summary) return false;
  const text = (summary.textContent || '').trim().toLowerCase();
  return SIZE_GUIDE_KEYWORDS.some((kw) => text.includes(kw));
}

function bindSizeGuide(context) {
  const fired = new WeakSet();

  document.querySelectorAll('accordion-custom details').forEach((details) => {
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!isSizeGuideAccordion(details)) return;

    details.addEventListener('toggle', () => {
      // Only fire when transitioning to open, and only once per page load to
      // avoid spam if the user keeps toggling.
      if (!details.open || fired.has(details)) return;
      fired.add(details);
      track('view_size_guide', payload(context, {
        location: 'pdp_accordion',
      }));
    });
  });
}

/** ---------- 3. image_zoom ---------- */
function bindImageZoom(context) {
  document.addEventListener(ThemeEvents.zoomMediaSelected, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    track('image_zoom', payload(context, {
      media_index: typeof detail.index === 'number' ? detail.index : null,
    }));
  });
}

/** ---------- 4. carousel_scroll ---------- */
// Heuristic: any click/touch within the media-gallery on a control button or
// a thumbnail counts as carousel navigation. Throttled to once per second to
// avoid storms from rapid clicks.
function bindCarouselScroll(context) {
  const gallery = document.querySelector('media-gallery');
  if (!gallery) return;

  let lastFiredAt = 0;
  const THROTTLE_MS = 1000;

  const handler = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Match on common slideshow/gallery navigation surfaces.
    const navMatch = target.closest('button, [role="button"], a, .slideshow-control, .thumbnails__item');
    if (!navMatch) return;
    // Ignore the zoom button, the ATC, etc. — those are not carousel nav.
    if (navMatch.closest('zoom-dialog, .add-to-cart-button, dialog')) return;

    const now = Date.now();
    if (now - lastFiredAt < THROTTLE_MS) return;
    lastFiredAt = now;

    track('carousel_scroll', payload(context, {
      trigger: navMatch.tagName.toLowerCase(),
    }));
  };

  gallery.addEventListener('click', handler, { passive: true });
}

/** ---------- 5. scroll_depth_pdp ---------- */
// Fire one event per threshold (25/50/75/100) per page load.
function bindScrollDepth(context) {
  const productInfo = document.querySelector('[data-testid="product-information"]');
  // Use the product information section as the reference for "PDP depth" rather
  // than the whole document, which would include the recommendations and footer.
  const reference = productInfo || document.body;

  const thresholds = [25, 50, 75, 100];
  const fired = new Set();

  function check() {
    const rect = reference.getBoundingClientRect();
    const totalHeight = rect.height;
    if (totalHeight <= 0) return;

    // How far past the top of the section the viewport bottom has reached.
    const viewportBottom = window.scrollY + window.innerHeight;
    const sectionTop = window.scrollY + rect.top;
    const scrolledIntoSection = Math.max(0, viewportBottom - sectionTop);
    const percent = Math.min(100, (scrolledIntoSection / totalHeight) * 100);

    for (const t of thresholds) {
      if (fired.has(t)) continue;
      if (percent >= t) {
        fired.add(t);
        track('scroll_depth_pdp', payload(context, { depth_percent: t }));
      }
    }

    if (fired.size === thresholds.length) {
      window.removeEventListener('scroll', onScroll);
    }
  }

  let scheduled = false;
  function onScroll() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      check();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  // Initial check in case the page is short enough that depth thresholds are
  // already crossed without any scroll event.
  check();
}

/** ---------- 6. atc_click_failed ---------- */
// Two failure modes:
//   (a) Button is disabled (variant not selected / sold out) — browsers
//       suppress click events on disabled buttons, so listen on pointerdown
//       at the document level using event capture.
//   (b) Server returned a cart error after a successful click — listen for
//       ThemeEvents.cartError.
function bindAtcFailed(context) {
  document.addEventListener('pointerdown', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.add-to-cart-button');
    if (!(button instanceof HTMLButtonElement)) return;
    if (!button.disabled) return;

    // Try to surface why it was disabled (sold out vs no variant chosen).
    const text = (button.textContent || '').trim().toLowerCase();
    const reason = text.includes('esgotado') || text.includes('sold')
      ? 'sold_out'
      : 'variant_not_selected';

    track('atc_click_failed', payload(context, { reason }));
  }, true);

  document.addEventListener(ThemeEvents.cartError, (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    track('atc_click_failed', payload(context, {
      reason: 'cart_error',
      message: typeof detail.message === 'string' ? detail.message.slice(0, 200) : null,
    }));
  });
}

/** ---------- pdp_loaded ---------- */
// Fires once on init with the full context snapshot (availability included).
// This is the baseline event for "a user saw this PDP with this availability
// state" — letting you segment downstream funnel events by
// landing_variant_available / availability_pct / oos_variants in GA4.
function emitPdpLoaded(context) {
  track('pdp_loaded', payload(context, {}));
}

/** ---------- Init ---------- */
function init() {
  const context = readProductContext();
  if (!context) return; // Not a PDP, or context tag missing.

  emitPdpLoaded(context);
  bindSelectVariant(context);
  bindSizeGuide(context);
  bindImageZoom(context);
  bindCarouselScroll(context);
  bindScrollDepth(context);
  bindAtcFailed(context);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
